<template>
  <form ref="formEl" novalidate :class="cn('flex flex-col gap-5', props.class)" @submit="onSubmit">
    <slot :values="values" :meta="meta" :submitting="isSubmitting" :submit="onSubmit" />
  </form>
</template>

<script setup lang="ts" generic="TValues extends Record<string, unknown>">
// A form over a zod schema: the <form> element, submit, and a focus on the first
// field in error after an invalid submit (in document order). The fields inside
// find the form by injection and bind by name; a valid submit emits the parsed
// values.
//
// Two ways to use it. The simple one passes `schema` and `initialValues` and
// KitForm owns the form. A page that needs the form's state outside the fields
// (a settings editor that seeds when a status arrives and compares for dirty)
// owns it with useKitForm and passes `form`. Either way nothing binds a model
// into the fields: a field is bound to its value by name, and a page changes a
// value only through the form's own methods.
import { FormContextKey } from "vee-validate";
import { computed, nextTick, provide, useTemplateRef, type HTMLAttributes } from "vue";
import type { z } from "zod";

import { cn } from "@/lib/utils";

import { KIT_FORM_LABELS, type LabelPlacement } from "./options";
import { KIT_FORM_CONTEXT, useKitForm, type KitFormContext } from "./useKitForm";

const props = defineProps<{
  // One or the other: a schema for a form KitForm owns, or a page's own form.
  schema?: z.ZodType<TValues, z.ZodTypeDef, unknown>;
  initialValues?: Partial<TValues>;
  form?: KitFormContext<TValues>;
  // "beside" puts each label left of its control once the field is wide enough.
  labels?: LabelPlacement;
  class?: HTMLAttributes["class"];
}>();

provide(
  KIT_FORM_LABELS,
  computed(() => props.labels ?? "above"),
);

const emit = defineEmits<{
  submit: [values: TValues];
  invalid: [];
}>();

const form: KitFormContext<TValues> = props.form ?? useKitForm<TValues>(() => props.schema!, props.initialValues);
if (props.form) provide(FormContextKey, props.form[KIT_FORM_CONTEXT] as never);
const { values, meta, isSubmitting, handleSubmit } = form;

const formEl = useTemplateRef<HTMLFormElement>("formEl");

const FOCUSABLE = "input:not([type=hidden]), select, textarea, button, [tabindex]:not([tabindex='-1'])";

async function focusFirstError() {
  await nextTick();
  const invalid = formEl.value?.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!invalid) return;
  const target = invalid.matches(FOCUSABLE) ? invalid : invalid.querySelector<HTMLElement>(FOCUSABLE);
  target?.focus();
}

const onSubmit = handleSubmit(
  (parsed) => emit("submit", parsed as TValues),
  () => {
    emit("invalid");
    void focusFirstError();
  },
);

// The view's handle: submit from a button outside the form (a wizard's Next),
// reset after a save, or seed values that arrived after mount.
defineExpose({
  submit: onSubmit,
  validate: form.validate,
  validateField: form.validateField,
  resetForm: form.resetForm,
  setValues: form.setValues,
  setFieldValue: form.setFieldValue,
  setFieldError: form.setFieldError,
  values,
  meta,
});
</script>
